import React, { FC, useMemo, useState } from 'react';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import { Button, Modal } from 'src/shared/ui';
import {
  WsFilterCondition,
  WsFilterOperator,
  WsFilterPrimitive,
} from 'src/shared/store/table/searchFilters';

type TableFiltersModalProps = {
  formStructure: FieldType[];
  value: WsFilterCondition[];
  onApply: (filters: WsFilterCondition[]) => void;
  onClose: () => void;
};

type FilterDraft = {
  id: number;
  field: string;
  op: WsFilterOperator;
  valueInput: string;
};

const FILTER_OPERATOR_OPTIONS: Array<{ value: WsFilterOperator; label: string }> = [
  { value: 'in', label: 'В списке' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'contains', label: 'Содержит' },
  { value: 'isnull', label: 'Пусто' },
  { value: 'notnull', label: 'Не пусто' },
];

const defaultFilterDraft = (id: number): FilterDraft => ({
  id,
  field: '',
  op: 'contains',
  valueInput: '',
});

const operatorRequiresValue = (operator: WsFilterOperator): boolean =>
  operator !== 'isnull' && operator !== 'notnull';

const splitValuesInput = (raw: string): string[] =>
  raw
    .split(/[,;\n]/)
    .map(value => value.trim())
    .filter(Boolean);

const parseBoolInput = (raw: string): boolean | null => {
  const normalized = raw.trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'да', 'д'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', 'нет', 'н'].includes(normalized)) return false;
  return null;
};

const parseFilterValue = (raw: string, columnType: FieldType['columnType']): WsFilterPrimitive => {
  const normalized = raw.trim();
  if (!normalized) {
    return '';
  }

  if (normalized.toLowerCase() === 'null') {
    return null;
  }

  if (columnType === 'boolean') {
    const boolValue = parseBoolInput(normalized);
    if (boolValue === null) {
      throw new Error(`Значение "${normalized}" должно быть true/false (или да/нет)`);
    }
    return boolValue;
  }

  if (columnType === 'number' || columnType === 'floatNumber') {
    const numericValue = Number(normalized);
    if (Number.isNaN(numericValue)) {
      throw new Error(`Значение "${normalized}" должно быть числом`);
    }
    return numericValue;
  }

  return normalized;
};

const toDrafts = (filters: WsFilterCondition[]): FilterDraft[] => {
  if (!filters.length) {
    return [defaultFilterDraft(1)];
  }

  return filters.map((filter, index) => {
    const valueInput = Array.isArray(filter.value)
      ? filter.value.map(value => String(value ?? '')).join(', ')
      : filter.value === undefined || filter.value === null
        ? ''
        : String(filter.value);

    return {
      id: index + 1,
      field: filter.field || '',
      op: filter.op || 'contains',
      valueInput,
    };
  });
};

export const TableFiltersModal: FC<TableFiltersModalProps> = ({
  formStructure,
  value,
  onApply,
  onClose,
}) => {
  const filterableFields = useMemo(
    () =>
      formStructure.filter(
        field =>
          field.columnName &&
          field.columnName.toLowerCase() !== 'id' &&
          field.columnType !== 'actions',
      ),
    [formStructure],
  );
  const fieldsByColumn = useMemo(() => {
    const map = new Map<string, FieldType>();
    filterableFields.forEach(field => {
      map.set(field.columnName, field);
    });
    return map;
  }, [filterableFields]);
  const [drafts, setDrafts] = useState<FilterDraft[]>(() => toDrafts(value));
  const [localError, setLocalError] = useState('');

  const updateDraft = (id: number, patch: Partial<FilterDraft>) => {
    setLocalError('');
    setDrafts(prev => prev.map(draft => (draft.id === id ? { ...draft, ...patch } : draft)));
  };

  const addDraft = () => {
    setDrafts(prev => [...prev, defaultFilterDraft(Date.now())]);
  };

  const removeDraft = (id: number) => {
    setLocalError('');
    setDrafts(prev => (prev.length <= 1 ? prev : prev.filter(draft => draft.id !== id)));
  };

  const clearAll = () => {
    setLocalError('');
    setDrafts([defaultFilterDraft(1)]);
  };

  const buildFilters = (): WsFilterCondition[] => {
    return drafts
      .filter(draft => draft.field.trim())
      .map(draft => {
        const field = fieldsByColumn.get(draft.field);
        if (!field) {
          throw new Error(`Неизвестная колонка фильтра: ${draft.field}`);
        }

        if (!operatorRequiresValue(draft.op)) {
          return {
            field: field.columnName,
            op: draft.op,
          };
        }

        const rawValues = splitValuesInput(draft.valueInput);
        if (!rawValues.length) {
          throw new Error(
            `Для фильтра "${field.columnLabel || field.columnName}" укажите значение`,
          );
        }

        if (draft.op === 'in') {
          return {
            field: field.columnName,
            op: draft.op,
            value: rawValues.map(rawValue => parseFilterValue(rawValue, field.columnType)),
          };
        }

        return {
          field: field.columnName,
          op: draft.op,
          value: parseFilterValue(rawValues[0], field.columnType),
        };
      });
  };

  const handleApply = () => {
    try {
      onApply(buildFilters());
      onClose();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Некорректные параметры фильтра');
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[64rem] max-w-[95dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">Фильтры таблицы</div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">
          Соберите условия для текущей формы. Фильтры применяются вместе с обычным текстовым поиском
          из строки выше.
        </div>

        <div className="mb-3 flex items-center">
          <div className="font-gothampro-500 text-sm dark:text-white">Условия</div>
          <Button size="small" variant="violetOutline" classNames="ml-auto w-36" onClick={addDraft}>
            Добавить условие
          </Button>
        </div>

        <div className="max-h-[22rem] overflow-y-auto rounded border border-grey-1000 p-3">
          {drafts.map(draft => (
            <div key={draft.id} className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-12">
              <div className="md:col-span-4">
                <select
                  className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  value={draft.field}
                  onChange={event => updateDraft(draft.id, { field: event.target.value })}
                >
                  <option value="">Выберите колонку</option>
                  {filterableFields.map(field => (
                    <option key={field.columnName} value={field.columnName}>
                      {field.columnLabel || field.columnName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <select
                  className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  value={draft.op}
                  onChange={event =>
                    updateDraft(draft.id, { op: event.target.value as WsFilterOperator })
                  }
                >
                  {FILTER_OPERATOR_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-4">
                <input
                  className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  value={draft.valueInput}
                  onChange={event => updateDraft(draft.id, { valueInput: event.target.value })}
                  disabled={!operatorRequiresValue(draft.op)}
                  placeholder={
                    draft.op === 'in'
                      ? 'Значения через запятую'
                      : operatorRequiresValue(draft.op)
                        ? 'Введите значение'
                        : 'Значение не требуется'
                  }
                />
              </div>

              <div className="flex items-center justify-end md:col-span-1">
                <Button
                  size="small"
                  variant="greyLight"
                  classNames="w-20"
                  onClick={() => removeDraft(draft.id)}
                  disabled={drafts.length <= 1}
                >
                  Удалить
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-grey-300">
          Поддерживаемые операторы: <code>=</code>, <code>!=</code>, <code>{'>'}</code>,{' '}
          <code>{'>='}</code>, <code>{'<'}</code>, <code>{'<='}</code>, <code>contains</code>,{' '}
          <code>in</code>, <code>isnull</code>, <code>notnull</code>.
        </div>

        {localError && <div className="mt-3 text-sm text-red-400">{localError}</div>}

        <div className="mt-5 flex gap-2">
          <Button size="small" variant="green" classNames="w-32" onClick={handleApply}>
            Применить
          </Button>
          <Button size="small" variant="violetOutline" classNames="w-40" onClick={clearAll}>
            Очистить всё
          </Button>
          <Button size="small" variant="greyLight" classNames="ml-auto w-28" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>
  );
};
